/**
 * Pre-Recording Setup Screen
 *
 * Displays age-based tips and preparation checklist before starting camera.
 * Helps users prepare for optimal recording conditions.
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  CheckCircle,
  FiberManualRecord,
  Videocam,
  Brightness6,
  PhonelinkOff,
  ChildCare
} from '@material-ui/icons';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    maxWidth: 700,
    margin: '0 auto'
  },
  paper: {
    padding: theme.spacing(3)
  },
  header: {
    marginBottom: theme.spacing(3),
    textAlign: 'center'
  },
  ageChip: {
    marginBottom: theme.spacing(2),
    fontSize: '0.9rem',
    fontWeight: 600
  },
  section: {
    marginBottom: theme.spacing(3)
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  checklistItem: {
    paddingLeft: 0
  },
  tipsList: {
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  tipItem: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5)
  },
  positionRecommendation: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2)
  },
  continueButton: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 4),
    fontSize: '1rem'
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  }
}));

interface PreRecordingSetupProps {
  infantDateOfBirth?: Date;
  onContinue: () => void;
  onCancel: () => void;
}

// Age-based tips and recommendations
interface AgeBasedGuidance {
  ageRangeLabel: string;
  recommendedPosition: string;
  tips: string[];
}

function getAgeInMonths(dob: Date): number {
  const now = new Date();
  const monthsDiff = (now.getFullYear() - dob.getFullYear()) * 12 + (now.getMonth() - dob.getMonth());
  return Math.max(0, monthsDiff);
}

function getAgeBasedGuidance(ageInMonths: number): AgeBasedGuidance {
  if (ageInMonths < 2) {
    return {
      ageRangeLabel: '0-2 months',
      recommendedPosition: 'Supine (on back)',
      tips: [
        'Place baby on their back on a firm, flat surface',
        'Remove any blankets or pillows from the area',
        'Ensure baby is awake and alert',
        'Try recording after a feeding when baby is content',
        'Allow at least 30 seconds of video for best results'
      ]
    };
  } else if (ageInMonths < 4) {
    return {
      ageRangeLabel: '2-4 months',
      recommendedPosition: 'Supine (on back)',
      tips: [
        'Place baby on their back on a play mat or firm surface',
        'Can include short tummy time sequences if baby tolerates it',
        'Capture natural movements - no need to stimulate',
        'Record for at least 45 seconds',
        'Multiple short clips are better than one long clip'
      ]
    };
  } else if (ageInMonths < 6) {
    return {
      ageRangeLabel: '4-6 months',
      recommendedPosition: 'Supine or Prone (back or tummy)',
      tips: [
        'Capture both supine and prone positions if possible',
        'Ensure baby has space to move arms and legs freely',
        'Watch for rolling attempts - these are valuable!',
        'Record during active play time',
        'Aim for 60 seconds of video'
      ]
    };
  } else if (ageInMonths < 9) {
    return {
      ageRangeLabel: '6-9 months',
      recommendedPosition: 'Prone (tummy), sitting, or moving',
      tips: [
        'Capture sitting attempts and crawling exploration',
        'Record during floor play time',
        'Include reaching and object manipulation',
        'Film from a stable position as baby may move around',
        'Record for 60-90 seconds'
      ]
    };
  } else if (ageInMonths < 12) {
    return {
      ageRangeLabel: '9-12 months',
      recommendedPosition: 'Sitting, crawling, or standing',
      tips: [
        'Capture cruising along furniture or walking attempts',
        'Record baby playing with toys while sitting',
        'Include transitions between positions',
        'Keep camera stable as baby moves',
        'Record for 90 seconds'
      ]
    };
  } else {
    return {
      ageRangeLabel: '12+ months',
      recommendedPosition: 'Standing, walking, or playing',
      tips: [
        'Film during active play',
        'Capture walking, running, or climbing activities',
        'Include fine motor activities like stacking blocks',
        'Ensure adequate space for movement',
        'Record for 90-120 seconds'
      ]
    };
  }
}

export default function PreRecordingSetup({
  infantDateOfBirth,
  onContinue,
  onCancel
}: PreRecordingSetupProps): JSX.Element {
  const classes = useStyles();
  const { t } = useTranslation();

  // Checklist state
  const [checklistCompleted, setChecklistCompleted] = useState({
    lighting: false,
    surface: false,
    distractions: false,
    position: false
  });

  // Calculate age and get guidance
  const { ageInMonths, guidance } = useMemo(() => {
    if (!infantDateOfBirth) {
      return {
        ageInMonths: null,
        guidance: {
          ageRangeLabel: 'All ages',
          recommendedPosition: 'Natural position for your baby',
          tips: [
            'Ensure good lighting - avoid backlighting',
            'Place baby on a safe, flat surface',
            'Remove blankets, pillows, and toys from view',
            'Keep camera steady',
            'Record for at least 60 seconds'
          ]
        }
      };
    }

    const months = getAgeInMonths(infantDateOfBirth);
    return {
      ageInMonths: months,
      guidance: getAgeBasedGuidance(months)
    };
  }, [infantDateOfBirth]);

  const allChecklistComplete = Object.values(checklistCompleted).every(v => v);

  const handleChecklistChange = (key: keyof typeof checklistCompleted) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setChecklistCompleted({
      ...checklistCompleted,
      [key]: event.target.checked
    });
  };

  return (
    <Box className={classes.container}>
      <Paper className={classes.paper}>
        {/* Header */}
        <Box className={classes.header}>
          <Videocam style={{ fontSize: 48, color: '#2196f3', marginBottom: 8 }} />
          <Typography variant="h5" gutterBottom>
            Prepare for Recording
          </Typography>
          {ageInMonths !== null && (
            <Chip
              label={`Baby's Age: ${guidance.ageRangeLabel}`}
              className={classes.ageChip}
              color="primary"
            />
          )}
        </Box>

        <Divider className={classes.divider} />

        {/* Recommended Position */}
        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            <ChildCare />
            Recommended Position
          </Typography>
          <Box className={classes.positionRecommendation}>
            <Typography variant="body1" style={{ fontWeight: 600 }}>
              {guidance.recommendedPosition}
            </Typography>
          </Box>
        </Box>

        {/* Age-based tips */}
        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Tips for Best Results
          </Typography>
          <List className={classes.tipsList}>
            {guidance.tips.map((tip, index) => (
              <ListItem key={index} className={classes.tipItem}>
                <ListItemIcon style={{ minWidth: 32 }}>
                  <FiberManualRecord style={{ fontSize: 8, color: '#2196f3' }} />
                </ListItemIcon>
                <ListItemText
                  primary={tip}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        <Divider className={classes.divider} />

        {/* Preparation Checklist */}
        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            <CheckCircle />
            Pre-Recording Checklist
          </Typography>
          <List>
            <ListItem className={classes.checklistItem}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checklistCompleted.lighting}
                    onChange={handleChecklistChange('lighting')}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">
                      <Brightness6 style={{ fontSize: 18, verticalAlign: 'middle', marginRight: 4 }} />
                      Good lighting (natural light or bright room)
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Avoid filming with windows or lights behind baby
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            <ListItem className={classes.checklistItem}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checklistCompleted.surface}
                    onChange={handleChecklistChange('surface')}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">
                      Safe, flat surface prepared
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Clear of blankets, pillows, or toys
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            <ListItem className={classes.checklistItem}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checklistCompleted.distractions}
                    onChange={handleChecklistChange('distractions')}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">
                      <PhonelinkOff style={{ fontSize: 18, verticalAlign: 'middle', marginRight: 4 }} />
                      Minimize distractions
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Turn off TV, silence notifications, reduce background noise
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            <ListItem className={classes.checklistItem}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checklistCompleted.position}
                    onChange={handleChecklistChange('position')}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">
                      Baby is in recommended position
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {guidance.recommendedPosition}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          </List>
        </Box>

        {/* Action Buttons */}
        <Box style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 24 }}>
          <Button onClick={onCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={onContinue}
            variant="contained"
            color="primary"
            className={classes.continueButton}
            disabled={!allChecklistComplete}
            startIcon={<Videocam />}
          >
            Start Camera
          </Button>
        </Box>

        {!allChecklistComplete && (
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ display: 'block', textAlign: 'center', marginTop: 8 }}
          >
            Complete all checklist items to continue
          </Typography>
        )}
      </Paper>
    </Box>
  );
}
